import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { VendorInitiationSummary } from '../../contexts/VmsWorkspaceContext';
import { isReturnToPreOnboardingJourney } from '../../pages/vms/preOnboardingInProgressModel';
import { shouldShowVmsServiceJourneyStrip } from '../../pages/vms/vmsServiceJourneyModel';
import { vmsInitiationChromeStatusBadgeClass } from '../../utils/vmsStatusBadge';
import { VmsServiceJourneyStripInline } from './VmsServiceJourneyStripInline';

function formatStatus(status: string): string {
  if (status === 'approved') return 'Approved';
  if (status === 'draft') return 'Draft';
  if (status === 'rejected') return 'Rejected';
  if (status === 'needs_more_information') return 'Need more information';
  if (status === 'pending_business_heads') return 'Pending business heads';
  return status;
}

/** Solid pills (Figma-style), uppercase in markup via `uppercase` */
function statusBadgeClass(status: string): string {
  return vmsInitiationChromeStatusBadgeClass(status);
}

interface VmsThreadHeaderProps {
  summary: VendorInitiationSummary | null;
  loading?: boolean;
  /** Override route-based visibility for the View Initiation link. */
  showViewInitiationLink?: boolean;
}

function isPreOnboardingPath(pathname: string): boolean {
  return pathname === '/vms/pre-onboarding' || pathname.startsWith('/vms/pre-onboarding/');
}

function shouldShowViewInitiationLink(pathname: string, search: string, state: unknown): boolean {
  if (isPreOnboardingPath(pathname)) return true;
  return isReturnToPreOnboardingJourney(new URLSearchParams(search), state);
}

function ChromeInset({ children }: { children: React.ReactNode }) {
  return (
    <div className="vms-thread-header-strip shrink-0 px-[var(--vms-chrome-gutter-x)] pb-2 pt-2 sm:pb-3 sm:pt-3">
      {children}
    </div>
  );
}

/**
 * Strip below the main header: active vendor initiation thread (service).
 * Card matches VMS reference — white shell, pink info cluster, solid status pill.
 */
const VmsThreadHeader: React.FC<VmsThreadHeaderProps> = ({
  summary,
  loading,
  showViewInitiationLink,
}) => {
  const location = useLocation();
  const showViewInitiation =
    showViewInitiationLink ??
    shouldShowViewInitiationLink(location.pathname, location.search, location.state);
  const showServiceJourneyStrip = shouldShowVmsServiceJourneyStrip(location.pathname);
  if (loading && !summary) {
    return (
      <ChromeInset>
        <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
          <p className="text-sm font-medium text-gray-500 animate-pulse">Loading service context…</p>
        </div>
      </ChromeInset>
    );
  }
  if (!summary) return null;

  const st = String(summary.status ?? '');
  const line = summary.service_name?.trim() || 'Untitled vendor service';
  const initiationApproved = st === 'approved';

  return (
    <ChromeInset>
      <div
        className={`flex min-h-[52px] flex-wrap items-center gap-x-2 gap-y-3 rounded-xl border border-primary-100 bg-white px-4 py-3 shadow-sm sm:min-h-[60px] sm:gap-x-3 sm:py-3 ${
          showViewInitiation ? 'sm:pr-2' : ''
        }`}
      >
        <div className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-2">
          <div className="inline-flex min-w-0 max-w-full flex-wrap items-center gap-2 rounded-lg bg-primary-50 px-3 h-[35px] sm:gap-2.5 sm:px-3.5">
            <span className="text-xs font-bold uppercase tracking-wide text-primary-800">Active service</span>
            <span className="select-none text-sm font-light text-gray-300" aria-hidden>
              |
            </span>
            <span className="min-w-0 text-sm font-medium text-gray-900 sm:max-w-[min(100%,28rem)] truncate" title={line}>
              {line}
            </span>
          </div>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1 text-[0.6875rem] font-bold uppercase tracking-wide ${statusBadgeClass(st)}`}
          >
            {formatStatus(st)}
          </span>
        </div>

        {showServiceJourneyStrip ? (
          <VmsServiceJourneyStripInline
            vendorInitiationId={summary.id}
            initiationApproved={initiationApproved}
          />
        ) : null}

        {showViewInitiation ? (
          <Link
            to={`/vms/vendor-initiation/${summary.id}`}
            className="vms-onboarding-chrome-link inline-flex w-fit shrink-0 items-center justify-center gap-1.5 rounded-lg border border-primary-200 bg-primary-50/80 px-3 h-[34px] text-sm font-normal !text-primary-800 transition hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 sm:ml-auto"
          >
            View Initiation
            <ChevronRight className="h-4 w-4 shrink-0 opacity-90" aria-hidden="true" />
          </Link>
        ) : null}
      </div>
    </ChromeInset>
  );
};

export default VmsThreadHeader;
